/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Address } from "./address";

export interface Merchant {
  id: number;
  merchantName: string;
  merchantLegalName: string;
  doingBusinessAs: string;
  externalMerchantId: number;
  merchantAddress: Address;
  remitToAddress?: RemitToAddress;
  statementOptions: { [index: string]: string };
  acceptedCreditCards: CreditCardEnum[];
  phoneNumber: string;
  maxTransactionLimit: number;
  accountDetails: AccountDetails;
  contractedRates: ContractedRates;
  merchantContractedRatesQueue: MerchantContractedRatesQueue;
  status: string;
  fraudVars: FraudVariables;
  deactivatedDate: Date;
  customerAccountNumber: string;
  alsoKnownAsName: string;
  primaryContactFirstName: string;
  primaryContactLastName?: string;
  primaryContactEmail: string;
  primaryContactPhoneNumber: string;
  legalEntityAddress: Address;
  legalEntityPhoneNumber: string;
  payAPICustomer: ApiCustomer;
  merchantAttributes: MerchantAttribute[];
  notifiedPayCustomerProvisioning: boolean;
  suppressZeroBalancePayment?: boolean;
  achEcheckEnabled?: boolean;
  amexEnabled?: boolean;
  merchantMigrationDetails?: MerchantMigrationDetails | null;
  migratedToInstaMed?: boolean;
  ngepmStatement?: boolean;
  balanceIncludesBadDebt?: boolean;
  clientName?: string;
  siteId?: string;
  practiceId?: string;
  practiceName?: string;
  enterpriseId?: string;
  enterprisePracticeId?: string;
  gcn?: string;
  locationName?: string;
  locationAlias?: string;
  locationId?: string;
  directBatching?: boolean;
}

export type Location = Pick<Merchant, "locationId" | "locationName" | "locationAlias">;

export interface RemitToAddress extends Address {
  merchantName: string;
}

export interface AccountDetails {
  routingNumber: string;
  accountNumber: string;
  accountType: string;
  checkingDepositType: string;
  amexSid: string;
  separateFundingAccounts: boolean;
  feeAccountDetails?: FeeAccountDetails;
  preferredProcessor: string;
  federalTaxId: string;
  worldPayAccountDetails: WorldPayAccountDetails;
  paypalAccountDetails: PaypalAccountDetails;
  qbPayAccountDetails: QBPayAccountDetails;
  instaMedAccountDetails: InstaMedAccountDetails;
  globalPaymentsAccountDetails?: GlobalPaymentsAccountDetails;
}

export interface ContractedRates {
  perTransAuthFee: number;
  perTransRefundFee: number;
  perTransECheckFee: number;
  qualifiedUpperBoundaryPercent: number;
  qualifiedFeePercent: number;
  midQualifiedUpperBoundaryPercent: number;
  midQualifiedFeePercent: number;
  nonQualifiedUpperBoundaryPercent: number;
  nonQualifiedFeePercent: number;
  amexPercent: number;
  suppressFeeSettlement: boolean;
  feeSettlementType: FeeSettlementTypeEnum;
  effectiveFeeTypeDate: Date;
}

export interface MerchantContractedRatesQueue {
  toPerTransAuthFee: number;
  toPerTransRefundFee: number;
  toQualifiedUpperBoundaryPercent: number;
  toQualifiedFeePercent: number;
  toMidQualifiedUpperBoundaryPercent: number;
  toMidQualifiedFeePercent: number;
  toNonQualifiedUpperBoundaryPercent: number;
  toNonQualifiedFeePercent: number;
  toAmexPercent: number;
  toFeeSettlementType: string;
  toSuppressFeeSettlement: boolean;
}

export interface FraudVariables {
  duplicateDollarTxCount: number;
  duplicateDollarTxAmount: number;
  excessiveVolumeIncreasePercent1vs90: number;
  excessiveVolumeIncreaseAmount1vs90: number;
  excessiveVolumeIncreasePercent1vs30: number;
  excessiveVolumeIncreaseAmount1vs30: number;
  excessiveVolumeIncreasePercent1vs7: number;
  excessiveVolumeIncreaseAmount1vs7: number;
  excessiveVolumeIncreasePercent7vs90: number;
  excessiveVolumeIncreaseAmount7vs90: number;
  dailyUnmatchedCreditsCount: number;
  dailyUnmatchedCreditsAmount: number;
  sameCardTxCount: number;
  sameCardTxAmount: number;
  totalDailyForeignCardCount: number;
  totalDailyForeignCardTxAmount: number;
  txLimitToAlert: number;
  singleForcedTxAmount: number;
  aggregateForcedTxAmount: number;
  staleTxDays: number;
}

export interface ApiCustomer {
  id: number;
  customerName: string;
  code?: string;
}

export interface MerchantAttribute {
  attributeType: MerchantAttributeType;
  attributeName: string;
  attributeValue: string;
}

export interface FeeAccountDetails {
  routingNumber: string;
  accountNumber: string;
  accountType: FeeAccountType;
}

export interface WorldPayAccountDetails {
  backingWPMerchantExists: boolean;
  merchantUUID: string;
  worldpayUnderwritingStatus: Status;
  chainCode: string;
  billingDescriptor: string;
  ownershipType: string;
  businessType: string;
  mccCode: string;
  businessEstablishedDate: string;
  websiteURL: string;
  beneficialOwners: BeneficialOwner[];
  contacts: Contact[];
  elementAccountId: string;
  elementAcceptorId: string;
  elementTerminalId: string;
  elementAccountToken: string;
  vantivIbmMid: string;
  vantivLitleIbfMid: string;
  vantivLitlePbfMid: string;
  subMerchantId: string;
}

export interface PaypalAccountDetails {
  paypalCardNotPresentUsername: string;
  paypalCardNotPresentPassword: string;
  paypalCardPresentUsername: string;
  paypalCardPresentPassword: string;
  paypalPartner: string;
}

export interface QBPayAccountDetails {
  qbpayRealmId: string;
  qbpayRefreshToken: string;
  qbpayRefreshTokenExpiry: string;
  qbmsMerchantTicket: string;
  qbmsBatchCloseTime: number;
  qbmsBatchCloseTimeOfDay: string;
}

export interface MerchantMigrationDetails {
  effectiveDate?: string | null;
  instaMedMmid?: number;
  migratedMerchantName?: string;
  migrationStatus?: string;
  previousMerchantName?: string;
  toBeMigrated?: boolean | null;
  previousMmid?: number;
  previousProcessor?: string;
  practiceId?: number;
  deleted?: boolean;
}

export interface InstaMedAccountDetails {
  merchantId: string;
  storeId: string;
  instaMedTerminalIDList: { [P in Type]?: string };
  clientId: string;
  clientSecret: string;
  accountId: string;
}

export interface GlobalPaymentsAccountDetails {
  globalPaymentsMerchantName: string;
  globalPaymentsSiteId: string;
  globalPaymentsKey: string;
}

export interface BeneficialOwner {
  beneficialOwnerId: number;
  firstName: string;
  lastName: string;
  middleInitial: string;
  title: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phoneNumber: string;
  ssn: string;
  percentOwned: number;
  dateOfBirth: string;
  email: string;
  ownerType: OwnerType;
}

export interface Contact {
  title: string;
  firstName: string;
  middleInitial: string;
  lastName: string;
  phoneNumber: string;
  phoneNumberExt: string;
  email: string;
  faxNumber: string;
  contactType: ContactType;
}

export type merchantMigrationDetailsTypes =
  | "effectiveDate"
  | "instaMedMmid"
  | "migrationStatus"
  | "previousMerchantName"
  | "toBeMigrated"
  | "previousMMID"
  | "previousProcessor";

export type CreditCardEnum = "VISA" | "MCARD" | "AMEX" | "DISCV" | "CARECRED";

export type FeeSettlementTypeEnum = "DAILY" | "MONTHLY";

export type MerchantAttributeType = "REFERENCE_ID";

export type FeeAccountType = "C" | "S";

export type Status = "Approved" | "Declined" | "Pending";

export type Type = "PATIENT_PORTAL" | "ENGAGE" | "VIRTUAL_VISITS";

export type OwnerType =
  | "ControlOwner"
  | "BeneficialOwner1"
  | "BeneficialOwner2"
  | "BeneficialOwner3"
  | "BeneficialOwner4"
  | "BeneficialOwner5"
  | "BeneficialOwner6"
  | "BeneficialOwner7"
  | "BeneficialOwner8"
  | "BeneficialOwner9"
  | "BeneficialOwner10";

export type ContactType = "CustomerService" | "TerminalDeployment";

export interface MerchantSearchSummary {
  id: number;
  merchantName: string;
  customerAccountNumber: string;
  practiceId: string;
  locationAlias: string;
  externalMerchantId?: string;
  status: string;
}
